import { observer } from "mobx-react-lite"
import * as yup from "yup"
import { useStores } from "../../../../../models/helpers/useStores"
import styles from "./styles.module.scss"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  deleteAgentProfile,
  getAgentProfile,
  upsertAgentProfile,
} from "../../../../../services/firebase-functions/agentProfile"
import CloseModalButton from "../../../../buttons/CloseModalButton"
import { DefaultSpinner } from "../../../../loaders/DefaultSpinner"
import UserAvatar from "../../../../atoms/UserAvatar"
import EditFilledIcon from "../../../../assets/icons/EditFilledIcon"
import { FormPhoneInput } from "../../../../atoms/FormPhoneInput"
import { useAgentLink } from "./useAgentLink"
import LinkOutline from "~icons/eva/link-outline"
import PlusOutline from "~icons/eva/plus-outline"
import EditOutline from "~icons/eva/edit-2-outline"
import FormTextArea from "../../../../atoms/FormTextArea"
import { useEffect, useMemo, useRef } from "react"
import { makeAutoObservable } from "mobx"
import { CruiseLineId, IUser } from "@cruisen/contract"
import { updateUser, updateUserDataInTrips } from "../../../../../services/firestore/user"
import { getAvatarDownloadUrl, uploadAvatar } from "../../../../../services/firestore/userAvatar"
import { getAuth } from "firebase/auth"
import { isPhoneValid } from "../../../../../utils/validation"
import {
  getAgencyConsortia,
  getCruiseLines,
  getHostAgencies,
  upsertAgencyConsortia,
  upsertHostAgency,
} from "../../../../../services/firebase-functions/dictionaries"
import classNames from "classnames"
import { FormSelectCreateableInput } from "../../../../atoms/FormSelectCreateableInput"
import { loadString } from "../../../../../utils/storage"
import { useNavigate } from "react-router-dom"
import Config from "../../../../../config"
import FormTextInput from "../../../../atoms/FormTextInput"
import * as Sentry from "@sentry/react"
import { signOut } from "../../../../../services/auth"
import { colors } from "@theme/colors"
import { SubscriptionDetails } from "./components/SubscriptionDetails"
import { addTestCollaboratorToTrip } from "../../../../../services/firestore/developerFunctions"
import { ModalWrapper } from "../../../../templates/ModalWrapper"
import { TermsAndConditionsModal } from "./components/TermsAndConditionsModal"
import { IntegrationDetails } from "./components/IntegrationDetails"
import { ConfirmationModal } from "../../../../modals/ConfirmationModal"

const compareCruiseLineIds = (ids1: CruiseLineId[], ids2: CruiseLineId[]) => {
  if (ids1.length !== ids2.length) {
    return false
  }

  for (let i = 0; i < ids1.length; i++) {
    if (ids1[i] !== ids2[i]) {
      return false
    }
  }
  return true
}

type AccountModalFormValues = {
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  shortBio: string
  favoriteTravelDestination: string
  bestTravelAdvice: string
  agencyName: string
  agencyPhoneNumber: string
  hostAgencyId: string
  consortiaId: string
  customTerms: string
}

const accountModalSchema = yup.object().shape({
  firstName: yup.string().required("-"),
  lastName: yup.string().required("-"),
  phoneNumber: yup.string().required("-"),
  email: yup.string().required("-"),
  shortBio: yup.string(),
  favoriteTravelDestination: yup.string(),
  bestTravelAdvice: yup.string(),
  agencyName: yup.string(),
  agencyPhoneNumber: yup.string(),
  hostAgencyId: yup.string(),
  consortiaId: yup.string(),
  customTerms: yup.string(),
})

type AccountScreenStoreProperties = {
  isSavingDetails: boolean
  imagePickerResponse: File | null
  newAvatarURL: string | null
  isModalWarningVisible: boolean
  isTermsAndConditionsModalVisible: boolean
  preferredCruiseLineIds: CruiseLineId[]
}

class AccountModalStore implements AccountScreenStoreProperties {
  isSavingDetails = false
  imagePickerResponse = null
  newAvatarURL = null
  isModalWarningVisible = false
  isTermsAndConditionsModalVisible = false
  preferredCruiseLineIds: CruiseLineId[] = []

  constructor() {
    makeAutoObservable(this)
  }

  setProperty<K extends keyof AccountScreenStoreProperties>(
    key: K,
    value: AccountScreenStoreProperties[K],
  ): void {
    ;(this as AccountScreenStoreProperties)[key] = value
  }
}

export const AccountModal = observer(function AccountModal({
  closeModal,
  openSubscriptionModal,
}: {
  closeModal: () => void
  openSubscriptionModal: () => void
}) {
  const rootStore = useStores()
  const { loggedInUser } = rootStore
  const currentUser = getAuth().currentUser
  const avatarInputRef = useRef<HTMLInputElement | null>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const forceSentryError = loadString("forceSentryError")

  const store = useMemo(() => new AccountModalStore(), [])

  const { data, isLoading } = useQuery({
    queryKey: ["agentProfile", loggedInUser.id],
    queryFn: () => getAgentProfile(loggedInUser.id),
  })

  const { data: cruiseLines, isLoading: isLoadingCruiseLines } = useQuery({
    queryKey: ["cruiseLines"],
    queryFn: () => getCruiseLines(),
  })

  const { data: hostAgencies, isLoading: isLoadingHostAgencies } = useQuery({
    queryKey: ["hostAgencies"],
    queryFn: () => getHostAgencies(),
  })

  const { data: consortia, isLoading: isLoadingConsortia } = useQuery({
    queryKey: ["consortia"],
    queryFn: () => getAgencyConsortia(),
  })

  const { mutate: mutateLogout, isPending } = useMutation({
    mutationFn: () => signOut(),
    onSuccess: () => {
      navigate("/")
    },
  })

  const { mutate: mutateDeleteAgentProfile, isPending: isDeletingAgentProfile } = useMutation({
    mutationFn: async () => {
      await deleteAgentProfile()
      queryClient.invalidateQueries({ queryKey: ["agentProfile", loggedInUser.id] })
      await signOut()
    },
    onSuccess: () => {
      navigate("/")
    },
  })

  const createHostAgencyOptionMutation = useMutation({
    mutationFn: async (hostAgencyName: string) => {
      const newHostAgency = await upsertHostAgency({ hostAgencyName })

      queryClient
        .invalidateQueries({ queryKey: ["hostAgencies"] })
        .catch((error) => console.error("Error invalidating host agencies query", error))

      return {
        value: String(newHostAgency.id),
        label: newHostAgency.hostAgencyName,
      }
    },
  })

  const createConsortiaOptionMutation = useMutation({
    mutationFn: async (consortiaName: string) => {
      const newConsortia = await upsertAgencyConsortia({ consortiaName })

      queryClient
        .invalidateQueries({ queryKey: ["consortia"] })
        .catch((error) => console.error("Error invalidating consortia query", error))

      return {
        value: String(newConsortia.id),
        label: newConsortia.consortiaName,
      }
    },
  })

  const { agentProfile } = data || {}
  const { copyLink, isLinkCopied, shareLink } = useAgentLink(agentProfile)

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { isValid, isDirty },
  } = useForm({
    defaultValues: {
      firstName: loggedInUser?.firstName,
      lastName: loggedInUser?.lastName,
      email: loggedInUser?.email,
      phoneNumber: loggedInUser?.phoneNumber || "",
      shortBio: agentProfile?.shortBio,
      favoriteTravelDestination: agentProfile?.favouriteTravelDestination,
      bestTravelAdvice: agentProfile?.bestTravelAdvice,
      agencyName: agentProfile?.agencyName,
      agencyPhoneNumber: agentProfile?.agencyPhoneNumber || "",
      hostAgencyId: String(agentProfile?.hostAgency?.id),
      consortiaId: String(agentProfile?.consortia?.id),
      customTerms: agentProfile?.customTerms || "",
    },
    resolver: yupResolver(accountModalSchema),
    mode: "all",
  })

  const [phoneNumber, agencyPhoneNumber, customTerms] = watch([
    "phoneNumber",
    "agencyPhoneNumber",
    "customTerms",
  ])
  const isPhoneNumberValid = isPhoneValid(phoneNumber)
  const isAgencyPhoneNumberValid = isPhoneValid(agencyPhoneNumber)

  const advisorCustomTerms = customTerms || agentProfile?.customTerms || ""

  // Reset the form values when the agent profile is loaded
  useEffect(() => {
    if (agentProfile) {
      reset(
        {
          firstName: loggedInUser?.firstName,
          lastName: loggedInUser?.lastName,
          phoneNumber: loggedInUser?.phoneNumber || "",
          email: loggedInUser?.email,
          shortBio: agentProfile?.shortBio,
          favoriteTravelDestination: agentProfile?.favouriteTravelDestination,
          bestTravelAdvice: agentProfile?.bestTravelAdvice,
          agencyName: agentProfile?.agencyName,
          agencyPhoneNumber: agentProfile?.agencyPhoneNumber || "",
          hostAgencyId: String(agentProfile?.hostAgency?.id),
          consortiaId: String(agentProfile?.consortia?.id),
          customTerms: agentProfile?.customTerms || "",
        },
        { keepDirty: false },
      )
      store.setProperty("preferredCruiseLineIds", agentProfile?.preferredCruiseLineIds.sort() || [])
    }
  }, [agentProfile, reset, loggedInUser])

  const handleUpdateAccount = async (formValues: AccountModalFormValues) => {
    store.setProperty("isSavingDetails", true)

    const updatedUserData: Partial<IUser> = {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      phoneNumber: formValues.phoneNumber,
      email: formValues.email,
    }

    try {
      // Upload avatar file to firebase storage
      if (store.imagePickerResponse) {
        await uploadAvatar(currentUser.uid, store.imagePickerResponse)
        const url = await getAvatarDownloadUrl(currentUser.uid)
        updatedUserData.avatar = url
      }

      await updateUserDataInTrips({
        userId: loggedInUser.id,
        updatedData: {
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          avatar: updatedUserData?.avatar || null,
        },
      })

      await updateUser(loggedInUser.id, updatedUserData)

      await upsertAgentProfile({
        agentProfile: {
          id: agentProfile.id,
          userId: loggedInUser.id,
          phoneNumber: updatedUserData.phoneNumber,
          email: updatedUserData.email,
          shortBio: formValues.shortBio,
          favouriteTravelDestination: formValues.favoriteTravelDestination,
          bestTravelAdvice: formValues.bestTravelAdvice,
          agencyName: formValues.agencyName,
          agencyPhoneNumber: isAgencyPhoneNumberValid ? formValues.agencyPhoneNumber : "",
          hostAgencyId: Number(formValues.hostAgencyId) || null,
          consortiaId: Number(formValues.consortiaId) || null,
          preferredCruiseLineIds: store.preferredCruiseLineIds,
          customTerms: formValues?.customTerms || "",
        },
      })

      // Clean up the new avatar URL
      if (store.newAvatarURL) {
        URL.revokeObjectURL(store.newAvatarURL)
        store.setProperty("newAvatarURL", null)
      }

      if (store.imagePickerResponse) {
        store.setProperty("imagePickerResponse", null)
      }

      rootStore.setProp("loggedInUser", {
        ...loggedInUser,
        ...updatedUserData,
      })

      queryClient
        .invalidateQueries({ queryKey: ["agentProfile", loggedInUser.id] })
        .catch((error) => console.error("Error invalidating agent profile query", error))
    } catch (error) {
      console.error(error)
    } finally {
      store.setProperty("isSavingDetails", false)
      closeModal()
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0]

      // Clean up the previous avatar URL
      if (store.newAvatarURL) {
        URL.revokeObjectURL(store.newAvatarURL)
      }

      store.setProperty("imagePickerResponse", selectedFile)
      store.setProperty("newAvatarURL", URL.createObjectURL(selectedFile))
    }
  }

  const handleCloseModal = () => {
    if (isChanged) {
      store.setProperty("isModalWarningVisible", true)
      return
    }

    if (store.newAvatarURL) {
      URL.revokeObjectURL(store.newAvatarURL)
    }
    closeModal()
  }

  const handleSelectCruiseLine = (cruiseLineId: CruiseLineId) => {
    if (store.preferredCruiseLineIds.includes(cruiseLineId)) {
      store.setProperty(
        "preferredCruiseLineIds",
        store.preferredCruiseLineIds.filter((id) => id !== cruiseLineId).sort(),
      )
    } else {
      store.setProperty(
        "preferredCruiseLineIds",
        [...store.preferredCruiseLineIds, cruiseLineId].sort(),
      )
    }
  }

  const sortedHostAgencies =
    hostAgencies
      ?.map((hostAgency) => ({
        value: String(hostAgency.id),
        label: hostAgency.hostAgencyName,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || []

  const sortedConsortia =
    consortia
      ?.map((consortia) => ({
        value: String(consortia.id),
        label: consortia.consortiaName,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || []

  const isChanged =
    isDirty ||
    store.newAvatarURL ||
    !compareCruiseLineIds(agentProfile?.preferredCruiseLineIds || [], store.preferredCruiseLineIds)

  return (
    <div className={styles.accountModal} data-testid="account-modal">
      <input
        type="file"
        ref={avatarInputRef}
        style={{ display: "none" }}
        onChange={(e) => handleFileChange(e)}
        accept=".jpg,.jpeg,.png"
      />
      <div className={styles.accountModalHeader}>
        <h2>My profile</h2>
        <CloseModalButton
          onClick={handleCloseModal}
          testId="close-account-modal-button"
          disabled={isPending || store.isModalWarningVisible || store.isSavingDetails}
        />
      </div>
      {isLoading || isPending ? (
        <div className={styles.loadingContainer}>
          <DefaultSpinner />
        </div>
      ) : (
        <div className={styles.accountModalBody}>
          {Config.environment === "development" ? (
            <div className={styles.sectionInfo}>
              <p className={styles.sectionTitle}>Dev actions</p>
              <div className={styles.devButtonsContainer}>
                <button
                  className={styles.devButton}
                  onClick={() => mutateDeleteAgentProfile()}
                  data-testid="delete-agent-profile-button"
                >
                  {isDeletingAgentProfile ? "Deleting..." : "Delete agent profile"}
                </button>
                <button className={styles.devButton} onClick={openSubscriptionModal}>
                  Open subscription modal
                </button>
                <button
                  className={styles.devButton}
                  data-testid="add-test-collaborator-button"
                  onClick={async () => {
                    await addTestCollaboratorToTrip({
                      trip: rootStore.activeTrip,
                    })
                  }}
                >
                  Add test collaborator
                </button>
              </div>
            </div>
          ) : null}
          {forceSentryError === "available" ? (
            <div className={styles.sectionInfo}>
              <p className={styles.sectionTitle}>Sentry actions</p>
              <button
                className={styles.devButton}
                onClick={() => {
                  Sentry.captureException("Forced error by dev")
                  console.log("Error has been captured")
                }}
              >
                Force sentry error
              </button>
            </div>
          ) : null}
          {agentProfile?.agentLink && (
            <>
              <div className={styles.accountLinkContainer}>
                <div className={styles.sectionInfo}>
                  <p className={styles.sectionTitle}>Your link</p>
                  <p className={styles.sectionDescription}>Invite clients to create new trips.</p>
                </div>
                <div className={styles.shareLinkContainer}>
                  <div className={styles.shareLinkWrapper}>
                    <p className={styles.shareLink}>{shareLink}</p>
                  </div>
                  <button className={styles.copyLinkButton} onClick={copyLink}>
                    <LinkOutline className={styles.copyButtonIcon} />
                    <p className={styles.copyButtonText}>{isLinkCopied ? "Copied" : "Copy link"}</p>
                  </button>
                </div>
              </div>
              <div className={styles.divider} />
            </>
          )}
          <div className={styles.personalInfoContainer}>
            <div className={styles.sectionInfo}>
              <p className={styles.sectionTitle}>Personal info</p>
              <p className={styles.sectionDescription}>Manage your personal details</p>
            </div>
            <div className={styles.personalInfoForm}>
              <div
                className={styles.avatarContainer}
                onClick={() => {
                  if (avatarInputRef.current) {
                    avatarInputRef.current.click()
                  }
                }}
              >
                <UserAvatar
                  avatar={store.newAvatarURL || loggedInUser.avatar}
                  customSize={130}
                  fontSize="40px"
                  nameInitals={`${loggedInUser.firstName[0]}${loggedInUser.lastName[0]}`}
                />
                <div className={styles.avatarEditButton}>
                  <div className={styles.avatarEditContainer}>
                    <EditFilledIcon />
                  </div>
                </div>
              </div>
              <FormTextInput
                control={control}
                name="firstName"
                label="First name"
                parentBackgroundColor={colors.backgroundSecondary}
              />
              <FormTextInput
                control={control}
                name="lastName"
                label="Last name"
                parentBackgroundColor={colors.backgroundSecondary}
              />
              <FormPhoneInput
                control={control}
                name="phoneNumber"
                label="Phone number"
                parentBackgroundColor={colors.backgroundSecondary}
              />
              <FormTextInput
                control={control}
                name="email"
                label="Email"
                readOnly
                disabled
                parentBackgroundColor={colors.backgroundTertiary}
              />
              <FormTextInput
                control={control}
                name="favoriteTravelDestination"
                label="Favorite travel destination"
                parentBackgroundColor={colors.backgroundSecondary}
              />
              <FormTextArea
                control={control}
                label="Best travel advice"
                name="bestTravelAdvice"
                rows={3}
                resize={false}
                parentBackgroundColor={colors.backgroundSecondary}
              />
              <FormTextArea
                control={control}
                label="Short bio"
                name="shortBio"
                rows={3}
                resize={false}
                parentBackgroundColor={colors.backgroundSecondary}
              />
            </div>
          </div>
          <div className={styles.divider} />
          <div className={styles.agencyInfoContainer}>
            <div className={styles.sectionInfo}>
              <p className={styles.sectionTitle}>Terms & Conditions</p>
              <p className={styles.sectionDescription}>
                Customize your general agreement requirements.
              </p>
            </div>
            <div className={styles.agencyInfoForm}>
              {advisorCustomTerms ? (
                <div className={styles.customTermsContainer}>
                  <div
                    dangerouslySetInnerHTML={{ __html: advisorCustomTerms }}
                    className={styles.customTermsContainerText}
                  />
                  <div className={styles.customTermsContainerGradient} />
                  <button
                    className={styles.customTermsContainerButton}
                    onClick={() => {
                      store.setProperty("isTermsAndConditionsModalVisible", true)
                    }}
                  >
                    <EditOutline className={styles.customTermsContainerButtonIcon} />
                    <p>Edit</p>
                  </button>
                </div>
              ) : (
                <button
                  className={styles.addCustomTermsButton}
                  onClick={() => {
                    store.setProperty("isTermsAndConditionsModalVisible", true)
                  }}
                >
                  <PlusOutline className={styles.addCustomTermsButtonIcon} />
                  <p className={styles.addCustomTermsButtonText}>Add</p>
                </button>
              )}
            </div>
          </div>
          <div className={styles.divider} />
          <div className={styles.agencyInfoContainer}>
            <div className={styles.sectionInfo}>
              <p className={styles.sectionTitle}>Agency info</p>
              <p className={styles.sectionDescription}>Manage your agency details</p>
            </div>
            <div className={styles.agencyInfoForm}>
              <FormTextInput
                control={control}
                name="agencyName"
                label="Agency name"
                parentBackgroundColor={colors.backgroundSecondary}
              />
              <FormPhoneInput
                control={control}
                name="agencyPhoneNumber"
                label="Phone number"
                parentBackgroundColor={colors.backgroundSecondary}
              />
              <FormSelectCreateableInput
                control={control}
                name="hostAgencyId"
                options={sortedHostAgencies}
                label="Host agency"
                createOptionMutation={createHostAgencyOptionMutation}
                isDisabled={isLoadingHostAgencies}
                parentBackgroundColor={
                  isLoadingHostAgencies ? colors.backgroundTertiary : colors.backgroundSecondary
                }
              />
              <FormSelectCreateableInput
                control={control}
                name="consortiaId"
                options={sortedConsortia}
                label="Consortia"
                createOptionMutation={createConsortiaOptionMutation}
                isDisabled={isLoadingConsortia}
                parentBackgroundColor={
                  isLoadingConsortia ? colors.backgroundTertiary : colors.backgroundSecondary
                }
              />
            </div>
          </div>
          {!isLoadingCruiseLines && cruiseLines.length ? (
            <>
              <div className={styles.divider} />
              <div className={styles.cruiseLinesContainer}>
                <div className={styles.sectionInfo}>
                  <p className={styles.sectionTitle}>Preferred cruise lines</p>
                  <p className={styles.sectionDescription}>
                    Your customers will only see the cruise lines you select here.
                  </p>
                </div>
                <div className={styles.cruiseLinesList}>
                  {cruiseLines.map((cruiseLine) => (
                    <button
                      className={classNames(
                        styles.cruiseLineItem,
                        store.preferredCruiseLineIds.includes(cruiseLine.id) &&
                          styles.cruiseLineItemSelected,
                      )}
                      key={cruiseLine.id}
                      onClick={() => handleSelectCruiseLine(cruiseLine.id)}
                    >
                      {cruiseLine.name}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : null}
          {rootStore.stripeSubscription?.id ? (
            <>
              <div className={styles.divider} />
              <SubscriptionDetails />
            </>
          ) : null}
          {rootStore.isCruisenDeveloper ? (
            <>
              <div className={styles.divider} />
              <IntegrationDetails />
            </>
          ) : null}
          <div className={styles.shortDivider} />
          <button className={styles.logoutButton} onClick={() => mutateLogout()}>
            Log out
          </button>
        </div>
      )}
      <div className={styles.accountModalFooter}>
        <button
          className={styles.cancelButton}
          onClick={handleCloseModal}
          disabled={isPending || store.isModalWarningVisible || store.isSavingDetails}
        >
          Cancel
        </button>
        <button
          className={styles.saveButton}
          disabled={
            !isValid ||
            isPending ||
            !isChanged ||
            store.isSavingDetails ||
            store.isModalWarningVisible ||
            !isPhoneNumberValid ||
            (agencyPhoneNumber && agencyPhoneNumber !== "+1" && !isAgencyPhoneNumberValid)
          }
          onClick={handleSubmit(handleUpdateAccount)}
        >
          Save changes
        </button>
      </div>
      {store.isModalWarningVisible && (
        <ConfirmationModal
          title="Unsaved changes"
          description="Are you sure you want to discard all unsaved changes?"
          onConfirm={() => {
            if (store.newAvatarURL) {
              URL.revokeObjectURL(store.newAvatarURL)
            }
            closeModal()
          }}
          closeModal={() => store.setProperty("isModalWarningVisible", false)}
          confirmButtonText="Discard"
          isLoading={false}
          testId="discard-unsaved-changes-modal"
          isPrimary={false}
        />
      )}
      {store.isTermsAndConditionsModalVisible && (
        <ModalWrapper>
          <TermsAndConditionsModal
            onClose={() => store.setProperty("isTermsAndConditionsModalVisible", false)}
            onSave={(termsContent) => {
              setValue("customTerms", termsContent, { shouldDirty: true })
              trigger("customTerms")
            }}
            defaultTermsContent={advisorCustomTerms}
          />
        </ModalWrapper>
      )}
    </div>
  )
})
